import { useEffect, useState } from 'react';
import { Upload, X, Facebook, Youtube, Search, Globe, Image, FileCode, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { getSettings, updateSettings } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    logo_data: '',
    favicon_data: '',
    site_name: 'DJ Services',
    company_name: 'DJ Services LLC',
    tagline: 'Making Your Events Unforgettable',
    hero_title: 'Your Event, Our Soundtrack',
    hero_subtitle: 'Professional DJ services for weddings, parties, and corporate events',
    facebook_url: '',
    youtube_url: '',
    // SEO Fields
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image_data: '',
    google_analytics_id: '',
    robots_index: true,
    robots_follow: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await getSettings();
      setFormData({
        logo_data: res.data.logo_data || '',
        favicon_data: res.data.favicon_data || '',
        site_name: res.data.site_name || 'DJ Services',
        company_name: res.data.company_name || 'DJ Services LLC',
        tagline: res.data.tagline || 'Making Your Events Unforgettable',
        hero_title: res.data.hero_title || 'Your Event, Our Soundtrack',
        hero_subtitle: res.data.hero_subtitle || 'Professional DJ services for weddings, parties, and corporate events',
        facebook_url: res.data.facebook_url || '',
        youtube_url: res.data.youtube_url || '',
        // SEO Fields
        seo_title: res.data.seo_title || '',
        seo_description: res.data.seo_description || '',
        seo_keywords: res.data.seo_keywords || '',
        og_image_data: res.data.og_image_data || '',
        google_analytics_id: res.data.google_analytics_id || '',
        robots_index: res.data.robots_index !== false,
        robots_follow: res.data.robots_follow !== false
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e, field = 'logo_data') => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = field === 'favicon_data' ? 512 * 1024 : 2 * 1024 * 1024; // 512KB for favicon, 2MB for others
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${field === 'favicon_data' ? '512KB' : '2MB'}`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateSettings(formData);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Site Settings
          </h1>
          <p className="text-neutral-500 mt-2">Manage your website branding, content, and SEO</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-neutral-200 pb-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${
              activeTab === 'general' 
                ? 'bg-neutral-900 text-white' 
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            data-testid="tab-general"
          >
            <Globe className="w-4 h-4 inline mr-2" />
            General
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('seo')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${
              activeTab === 'seo' 
                ? 'bg-neutral-900 text-white' 
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            data-testid="tab-seo"
          >
            <Search className="w-4 h-4 inline mr-2" />
            SEO & Meta
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('social')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${
              activeTab === 'social' 
                ? 'bg-neutral-900 text-white' 
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            data-testid="tab-social"
          >
            <Image className="w-4 h-4 inline mr-2" />
            Social & Sharing
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-w-3xl">
          
          {/* General Tab */}
          {activeTab === 'general' && (
            <>
              {/* Logo & Favicon Section */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Branding
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div>
                    <Label className="mb-2 block">Site Logo</Label>
                    <div className="border-2 border-dashed border-neutral-200 rounded-lg p-4">
                      {formData.logo_data ? (
                        <div className="relative inline-block">
                          <img 
                            src={formData.logo_data} 
                            alt="Logo preview"
                            className="max-h-20 bg-white p-2 rounded"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            className="absolute -top-2 -right-2 w-6 h-6"
                            onClick={() => setFormData(prev => ({ ...prev, logo_data: '' }))}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center py-4">
                          <Upload className="w-6 h-6 text-neutral-400 mx-auto mb-2" />
                          <p className="text-xs text-neutral-500">Upload logo (max 2MB)</p>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, 'logo_data')}
                            data-testid="logo-upload-input"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Favicon Upload */}
                  <div>
                    <Label className="mb-2 block">Site Icon (Favicon)</Label>
                    <div className="border-2 border-dashed border-neutral-200 rounded-lg p-4">
                      {formData.favicon_data ? (
                        <div className="relative inline-block">
                          <img 
                            src={formData.favicon_data} 
                            alt="Favicon preview"
                            className="w-16 h-16 bg-white p-2 rounded object-contain"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            className="absolute -top-2 -right-2 w-6 h-6"
                            onClick={() => setFormData(prev => ({ ...prev, favicon_data: '' }))}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center py-4">
                          <FileCode className="w-6 h-6 text-neutral-400 mx-auto mb-2" />
                          <p className="text-xs text-neutral-500">Upload favicon (max 512KB)</p>
                          <p className="text-xs text-neutral-400">32x32 or 64x64 PNG recommended</p>
                          <input
                            type="file"
                            accept="image/png,image/x-icon,image/ico,.ico"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, 'favicon_data')}
                            data-testid="favicon-upload-input"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Site Info */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Site Information
                </h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="site_name">Site Name</Label>
                    <Input
                      id="site_name"
                      value={formData.site_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_name: e.target.value }))}
                      placeholder="Your DJ Company Name"
                      data-testid="site-name-input"
                    />
                    <p className="text-xs text-neutral-500">Displayed in the header/navbar</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name (for Copyright)</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Your Legal Company Name LLC"
                      data-testid="company-name-input"
                    />
                    <p className="text-xs text-neutral-500">Displayed in the footer copyright</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tagline">Tagline</Label>
                    <Input
                      id="tagline"
                      value={formData.tagline}
                      onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                      placeholder="A short tagline for your business"
                      data-testid="tagline-input"
                    />
                  </div>
                </div>
              </div>

              {/* Hero Section */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Hero Section
                </h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="hero_title">Hero Title</Label>
                    <Input
                      id="hero_title"
                      value={formData.hero_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, hero_title: e.target.value }))}
                      placeholder="Main headline for your homepage"
                      data-testid="hero-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
                    <Input
                      id="hero_subtitle"
                      value={formData.hero_subtitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                      placeholder="Supporting text below the headline"
                      data-testid="hero-subtitle-input"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <>
              {/* Page Title & Description */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Search className="w-5 h-5 inline mr-2" />
                  Search Engine Optimization
                </h2>
                <p className="text-sm text-neutral-500 mb-4">
                  These settings help search engines understand and display your website in search results.
                </p>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="seo_title">SEO Title (Page Title)</Label>
                    <Input
                      id="seo_title"
                      value={formData.seo_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo_title: e.target.value }))}
                      placeholder="DJ Services | Professional Event Entertainment"
                      maxLength={60}
                      data-testid="seo-title-input"
                    />
                    <p className="text-xs text-neutral-500">
                      Appears in browser tabs and search results. Recommended: 50-60 characters. 
                      <span className={formData.seo_title.length > 60 ? 'text-red-500' : 'text-neutral-400'}>
                        {' '}({formData.seo_title.length}/60)
                      </span>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seo_description">Meta Description</Label>
                    <Textarea
                      id="seo_description"
                      value={formData.seo_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo_description: e.target.value }))}
                      placeholder="Professional DJ services for weddings, corporate events, and private parties. Book your event today!"
                      rows={3}
                      maxLength={160}
                      data-testid="seo-description-input"
                    />
                    <p className="text-xs text-neutral-500">
                      Shown in search results below the title. Recommended: 150-160 characters.
                      <span className={formData.seo_description.length > 160 ? 'text-red-500' : 'text-neutral-400'}>
                        {' '}({formData.seo_description.length}/160)
                      </span>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seo_keywords">Meta Keywords</Label>
                    <Input
                      id="seo_keywords"
                      value={formData.seo_keywords}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo_keywords: e.target.value }))}
                      placeholder="dj services, wedding dj, corporate events, party dj"
                      data-testid="seo-keywords-input"
                    />
                    <p className="text-xs text-neutral-500">
                      Comma-separated keywords related to your business (less important for modern SEO but still used by some search engines)
                    </p>
                  </div>
                </div>
              </div>

              {/* Search Engine Preview */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Search Result Preview
                </h2>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-blue-700 text-lg hover:underline cursor-pointer truncate">
                    {formData.seo_title || formData.site_name || 'Your Site Title'}
                  </div>
                  <div className="text-green-700 text-sm truncate">
                    yourwebsite.com
                  </div>
                  <div className="text-neutral-600 text-sm mt-1 line-clamp-2">
                    {formData.seo_description || 'Add a meta description to see how your site will appear in search results.'}
                  </div>
                </div>
              </div>

              {/* Robots & Indexing */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Search Engine Indexing
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                    <div>
                      <Label className="text-base">Allow Search Indexing</Label>
                      <p className="text-xs text-neutral-500 mt-1">
                        Allow search engines like Google to index your site
                      </p>
                    </div>
                    <Switch
                      checked={formData.robots_index}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, robots_index: checked }))}
                      data-testid="robots-index-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                    <div>
                      <Label className="text-base">Allow Link Following</Label>
                      <p className="text-xs text-neutral-500 mt-1">
                        Allow search engines to follow links on your site
                      </p>
                    </div>
                    <Switch
                      checked={formData.robots_follow}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, robots_follow: checked }))}
                      data-testid="robots-follow-switch"
                    />
                  </div>
                </div>
              </div>

              {/* Google Analytics */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <BarChart3 className="w-5 h-5 inline mr-2" />
                  Google Analytics
                </h2>
                <div className="space-y-2">
                  <Label htmlFor="google_analytics_id">Google Analytics Measurement ID</Label>
                  <Input
                    id="google_analytics_id"
                    value={formData.google_analytics_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, google_analytics_id: e.target.value }))}
                    placeholder="G-XXXXXXXXXX"
                    data-testid="google-analytics-input"
                  />
                  <p className="text-xs text-neutral-500">
                    Enter your GA4 Measurement ID to track visitor analytics via Google Analytics.
                    Find it in your Google Analytics account under Admin → Data Streams.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Social & Sharing Tab */}
          {activeTab === 'social' && (
            <>
              {/* Social Media Links */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Social Media Links
                </h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="facebook_url" className="flex items-center gap-2">
                      <Facebook className="w-4 h-4 text-blue-600" />
                      Facebook URL
                    </Label>
                    <Input
                      id="facebook_url"
                      value={formData.facebook_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, facebook_url: e.target.value }))}
                      placeholder="https://www.facebook.com/yourpage"
                      data-testid="facebook-url-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="youtube_url" className="flex items-center gap-2">
                      <Youtube className="w-4 h-4 text-red-600" />
                      YouTube URL
                    </Label>
                    <Input
                      id="youtube_url"
                      value={formData.youtube_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                      placeholder="https://www.youtube.com/@yourchannel"
                      data-testid="youtube-url-input"
                    />
                  </div>
                </div>
              </div>

              {/* Open Graph / Social Sharing Image */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Image className="w-5 h-5 inline mr-2" />
                  Social Sharing Image (Open Graph)
                </h2>
                <p className="text-sm text-neutral-500 mb-4">
                  This image appears when your site is shared on Facebook, Twitter, LinkedIn, etc.
                </p>
                <div className="border-2 border-dashed border-neutral-200 rounded-lg p-6">
                  {formData.og_image_data ? (
                    <div className="relative inline-block">
                      <img 
                        src={formData.og_image_data} 
                        alt="OG Image preview"
                        className="max-h-48 bg-white rounded"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute -top-2 -right-2"
                        onClick={() => setFormData(prev => ({ ...prev, og_image_data: '' }))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block text-center">
                      <Image className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                      <p className="text-sm text-neutral-500">Click to upload social sharing image</p>
                      <p className="text-xs text-neutral-400 mt-1">Recommended: 1200x630 pixels (1.91:1 ratio)</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'og_image_data')}
                        data-testid="og-image-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Social Share Preview */}
              <div className="admin-card mb-6">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Social Share Preview
                </h2>
                <div className="bg-neutral-100 rounded-lg overflow-hidden max-w-md">
                  {formData.og_image_data ? (
                    <img 
                      src={formData.og_image_data} 
                      alt="Share preview"
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-neutral-200 flex items-center justify-center">
                      <Image className="w-12 h-12 text-neutral-400" />
                    </div>
                  )}
                  <div className="p-4 bg-white">
                    <p className="text-xs text-neutral-500 uppercase tracking-wide">yourwebsite.com</p>
                    <p className="font-semibold text-neutral-900 mt-1">
                      {formData.seo_title || formData.site_name || 'Your Site Title'}
                    </p>
                    <p className="text-sm text-neutral-500 mt-1 line-clamp-2">
                      {formData.seo_description || 'Your site description will appear here.'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Save Button - Always visible */}
          <div className="flex justify-end sticky bottom-4">
            <Button 
              type="submit" 
              className="bg-neutral-900 hover:bg-neutral-800 shadow-lg"
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save All Settings'
              )}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
